import {
  createContext,
  useContext,
  useRef,
  type MutableRefObject,
  type ReactNode,
} from "react";
import type { TooltipDatum } from "../types/atlasTooltip";
import { useTooltipState } from "./useTooltipState";
import type { UseTooltipStateReturn } from "./useTooltipState";

export type TooltipDispatchMethods<T = TooltipDatum> = Pick<
  UseTooltipStateReturn<T>,
  "showTooltip" | "hideTooltip" | "fixTooltip" | "clearFixed"
>;

const TooltipDispatchRefContext = createContext<MutableRefObject<
  TooltipDispatchMethods<TooltipDatum>
> | null>(null);

const TooltipPayloadContext = createContext<
  UseTooltipStateReturn<TooltipDatum>["tooltipState"] | undefined
>(undefined);

export interface TooltipStateIsolationProviderProps {
  children: ReactNode;
  onHighlight?: (item: string | undefined) => void;
  /**
   * When this value changes (by reference), tooltip state is cleared
   * synchronously during render. Replaces the `key={chartKey}` pattern.
   */
  dataVersion?: unknown;
}

/**
 * Splits tooltip concerns so chart memoization stays effective:
 * - `useTooltipDispatchRef()` returns a **stable ref** (identity never changes).
 *   Event handlers should call `ref.current.showTooltip(...)` etc. so charts do not
 *   subscribe to tooltip payload updates.
 * - `useIsolatedTooltipPayload()` subscribes to tooltip state for portal UI only.
 *
 * Compose as: Provider → tooltip shell (consumes payload) → memo(Chart) (uses dispatch ref).
 */
export function TooltipStateIsolationProvider({
  children,
  onHighlight,
  dataVersion,
}: TooltipStateIsolationProviderProps) {
  const api = useTooltipState(onHighlight, dataVersion);
  const dispatchRef = useRef<TooltipDispatchMethods<TooltipDatum>>(api);
  dispatchRef.current = {
    showTooltip: api.showTooltip,
    hideTooltip: api.hideTooltip,
    fixTooltip: api.fixTooltip,
    clearFixed: api.clearFixed,
  };

  return (
    <TooltipDispatchRefContext.Provider value={dispatchRef}>
      <TooltipPayloadContext.Provider value={api.tooltipState}>
        {children}
      </TooltipPayloadContext.Provider>
    </TooltipDispatchRefContext.Provider>
  );
}

export function useTooltipDispatchRef<
  T = TooltipDatum,
>(): MutableRefObject<TooltipDispatchMethods<T>> {
  const ref = useContext(TooltipDispatchRefContext) as MutableRefObject<
    TooltipDispatchMethods<T>
  > | null;
  if (!ref) {
    throw new Error(
      "useTooltipDispatchRef must be used within TooltipStateIsolationProvider",
    );
  }
  return ref;
}

/**
 * Like {@link useTooltipDispatchRef} but returns `null` instead of throwing
 * when used outside a {@link TooltipStateIsolationProvider}. This lets library
 * chart components opt into the tooltip system without requiring it.
 */
export function useOptionalTooltipDispatchRef<
  T = TooltipDatum,
>(): MutableRefObject<TooltipDispatchMethods<T>> | null {
  return useContext(TooltipDispatchRefContext) as MutableRefObject<
    TooltipDispatchMethods<T>
  > | null;
}

export function useIsolatedTooltipPayload<
  T = TooltipDatum,
>(): UseTooltipStateReturn<T>["tooltipState"] {
  return useContext(TooltipPayloadContext) as UseTooltipStateReturn<T>["tooltipState"];
}
